import { IMoveHandler, MoveHandlerParams } from "./MoveHandler";
import { PieceType, ISideEffect, EffectType, IPiece } from "../../../types";
import { EventType, SelectPieceEvent } from "../../../const";

export class ConsumeMoveHandler implements IMoveHandler {
  handleMove(params: MoveHandlerParams): void {
    // Validate this is a monkey doing a consume
    if (params.piece.type !== PieceType.Monkey) {
      throw new Error("ConsumeMoveHandler can only be used with Monkey pieces");
    }

    // Get the fungi being consumed
    const fungi = params.movementManager
      .getGameStateManager()
      .getBoardState()[params.toPosition.row][params.toPosition.col]
      .Pieces.find((p: IPiece) => p.type === PieceType.Fungi);

    if (!fungi) {
      throw new Error("ConsumeMoveHandler requires a fungi at the target position");
    }


    // Create side effects array to collect any effects from captureMove
    const sideEffects: ISideEffect[] = [];

    // Perform the capture movement (this handles the actual fungi removal)
    params.movementManager.captureMove(
      params.fromPosition,
      params.toPosition,
      params.piece,
      fungi,
      sideEffects // Allow captureMove to add capture side effects for fungi removal
    );

    // Filter out any zombify effects - we handle zombification separately in the enemy fungi check below
    const filteredSideEffects = sideEffects.filter(
      effect => effect.effect.type !== EffectType.zombify
    );

    // Process the remaining side effects (mainly the capture effect to remove fungi)
    filteredSideEffects.forEach((sideEffect) => {
      if (sideEffect.effect.type === EffectType.capture) {
        sideEffect.piecesToEffect.forEach((capturedPiece) => {
          // Release any entangled pieces if an octopus is captured
          if (
            capturedPiece.type === PieceType.Octopus &&
            capturedPiece.entangledPieceId
          ) {
            const entangledPiece = params.movementManager
              .getGameStateManager()
              .findPieceById(capturedPiece.entangledPieceId);
            if (entangledPiece) {
              entangledPiece.isEntangled = false;
            }
          }

          // Remove the captured piece (fungi) from the board
          params.movementManager
            .getGameStateManager()
            .removePiece(sideEffect.position, capturedPiece);
        });
      }
    });

    // CRITICAL: Only grant bonus turn if the monkey survived (safety check)
    const monkeyPosition = params.movementManager
      .getGameStateManager()
      .findPiecePosition(params.piece);

    if (monkeyPosition) {
      // CRITICAL: Clear any swing return block since we made a non-swing move
      // This allows the monkey to swing to new positions after consuming
      params.movementManager
        .getGameStateManager()
        .clearSwingReturnBlock();

      // Grant bonus turn for consuming any fungi (friendly or enemy)
      // ALLOW CHAINING: Consume can grant a bonus turn even during an existing bonus turn
      params.movementManager
        .getGameStateManager()
        .grantBonusTurn(params.piece);


      // MONKEY-CONSUME-FIX: Schedule zombification for AFTER the bonus turn move completes
      // Zombification is applied in MovementManager's OLD move handling path, right before FEN encoding,
      // so the bonus move's FEN correctly shows the monkey as zombified.
      // Skip when the Monkey is already zombified: a chained consume on a zombified Monkey
      // (e.g. via swing-leap chaining during its zombified turn) shouldn't re-schedule zombification.
      const isEnemyFungi = (params.piece.isLight && !fungi.isLight) || (!params.piece.isLight && fungi.isLight);
      if (isEnemyFungi && !params.piece.isZombified) {
        const gameState = params.movementManager.getGameStateManager().getGameState();
        (gameState as any).pendingMonkeyZombification = {
          pieceId: params.piece.id,
          reason: 'consume_enemy_fungi'
        };
      }

      // Auto-select the monkey for the bonus turn (preserve locked selection behavior)
      setTimeout(() => {
        // CRITICAL: Use the toPosition directly since we know where the monkey moved
        // After Firebase sync, pieces get new IDs, so we can't rely on the old piece reference
        const currentGameState = params.movementManager.getGameStateManager().getGameState();

        // The monkey is at toPosition (where it just consumed)
        const square = currentGameState.board[params.toPosition.row][params.toPosition.col];
        const currentMonkey = square.Pieces.find(p => p.type === PieceType.Monkey && p.isLight === params.piece.isLight);

        if (!currentMonkey) {
          return;
        }

        // CRITICAL FIX: Update activeBonusTurnPieceId with the NEW piece ID after Firebase sync
        // This ensures getValidMoves() will allow the monkey to move during the bonus turn
        const gameState = params.movementManager.getGameStateManager().getGameState();
        gameState.activeBonusTurnPieceId = currentMonkey.id;

        // MONKEY-CONSUME-FIX: Update pendingMonkeyZombification pieceId after Firebase sync
        if ((gameState as any).pendingMonkeyZombification?.pieceId === params.piece.id) {
          (gameState as any).pendingMonkeyZombification.pieceId = currentMonkey.id;
        }

        const selectEvent: SelectPieceEvent = {
          type: EventType.selectPiece,
          pieceId: currentMonkey.id, // Use the CURRENT piece ID from the synced state
          position: params.toPosition, // Use the position where the monkey moved to
          lockSelection: true, // Lock selection to prevent moving other pieces
        };


        // Emit event through EventManager (no more direct DOM manipulation)
        params.movementManager.getEventManager().emitEvent(selectEvent);
      }, 300); // 300ms delay to ensure the move animation completes
    } else {
    }
  }
}